import { useState } from 'react';
import { X, User, Mail, Lock, Phone, Scissors } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthModal = ({ open, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState('customer'); // 'customer' or 'barber'
  const [activeTab, setActiveTab] = useState('signin');
  
  const [signInData, setSignInData] = useState({
    email: '',
    password: ''
  });
  
  const [signUpData, setSignUpData] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: ''
  });

  const handleCustomerSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const user = {
        id: 'user_' + Date.now(),
        name: signInData.email.split('@')[0],
        email: signInData.email,
        type: 'customer'
      };
      
      localStorage.setItem('barbergo_user', JSON.stringify(user));
      toast.success(`Welcome back, ${user.name}!`);
      
      if (onSuccess) {
        onSuccess(user);
      }
      
      onClose();
      setTimeout(() => navigate('/customer-dashboard'), 500);
      setSignInData({ email: '', password: '' });
    } catch (error) {
      console.error('Sign in error:', error);
      toast.error('Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBarberSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // For demo - check if barber exists
      const response = await axios.get(`${API}/barbers`);
      const barber = response.data.find(b => b.email === signInData.email);
      
      if (barber) {
        localStorage.setItem('barbergo_barber', JSON.stringify(barber));
        toast.success(`Welcome back, ${barber.full_name}!`);
        onClose();
        setTimeout(() => navigate('/barber-dashboard'), 500);
      } else {
        toast.error('Barber account not found. Please sign up first.');
      }
      
      setSignInData({ email: '', password: '' });
    } catch (error) {
      console.error('Barber sign in error:', error);
      toast.error('Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    
    if (signUpData.password !== signUpData.confirm_password) {
      toast.error('Passwords do not match!');
      return;
    }
    
    if (signUpData.password.length < 6) {
      toast.error('Password must be at least 6 characters!');
      return;
    }
    
    setLoading(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const user = {
        id: 'user_' + Date.now(),
        name: signUpData.full_name,
        email: signUpData.email,
        phone: signUpData.phone,
        type: 'customer'
      };
      
      localStorage.setItem('barbergo_user', JSON.stringify(user));
      toast.success(`Welcome to BarberGo, ${user.name}!`);
      
      if (onSuccess) {
        onSuccess(user);
      }
      
      onClose();
      setSignUpData({
        full_name: '',
        email: '',
        phone: '',
        password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Sign up error:', error);
      toast.error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fillDemoCredentials = () => {
    if (userType === 'customer') {
      setSignInData({
        email: 'demo@barbergo.com',
        password: 'demo123'
      });
    } else {
      setSignInData({
        email: 'chidi.okafor@barbergo.com',
        password: 'barber123'
      });
    }
    toast.info('Demo credentials filled!');
  };

  if (!open) return null;

  return (
    <div data-testid="auth-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-[var(--bg-secondary)] dark:bg-[#1A1A1A] border border-[var(--border-color)] rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-[var(--bg-secondary)] dark:bg-[#1A1A1A] border-b border-[var(--border-color)] p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-[var(--text-primary)]" data-testid="auth-modal-title">
              {activeTab === 'signin' ? 'Welcome Back' : 'Create Account'}
            </h2>
            <button
              onClick={onClose}
              data-testid="close-auth-modal"
              className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* User Type Toggle */}
          <div className="grid grid-cols-2 gap-2 p-1 bg-[var(--bg-primary)] dark:bg-[#111111] rounded-lg">
            <button
              onClick={() => setUserType('customer')}
              data-testid="user-type-customer"
              className={`flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-semibold transition-all ${
                userType === 'customer'
                  ? 'bg-[#00C167] text-white shadow-lg'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
            >
              <User className="w-4 h-4" />
              Customer
            </button>
            <button
              onClick={() => setUserType('barber')}
              data-testid="user-type-barber"
              className={`flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-semibold transition-all ${
                userType === 'barber'
                  ? 'bg-[#00C167] text-white shadow-lg'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
            >
              <Scissors className="w-4 h-4" />
              Barber
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="signin" data-testid="signin-tab">Sign In</TabsTrigger>
              <TabsTrigger value="signup" data-testid="signup-tab">{userType === 'barber' ? 'Apply' : 'Sign Up'}</TabsTrigger>
            </TabsList>

            {/* Sign In Form */}
            <TabsContent value="signin">
              <form onSubmit={userType === 'customer' ? handleCustomerSignIn : handleBarberSignIn} className="space-y-4">
                <div>
                  <Label htmlFor="signin-email" className="text-[var(--text-primary)]">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="signin-email"
                      data-testid="signin-email-input"
                      type="email"
                      required
                      placeholder="your@email.com"
                      value={signInData.email}
                      onChange={(e) => setSignInData({ ...signInData, email: e.target.value })}
                      className="pl-10 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)] focus:border-[#00C167] focus:ring-[#00C167]/20"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="signin-password" className="text-[var(--text-primary)]">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="signin-password"
                      data-testid="signin-password-input"
                      type="password"
                      required
                      placeholder="••••••••"
                      value={signInData.password}
                      onChange={(e) => setSignInData({ ...signInData, password: e.target.value })}
                      className="pl-10 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)] focus:border-[#00C167] focus:ring-[#00C167]/20"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={fillDemoCredentials}
                    className="text-[#00C167] hover:underline font-medium"
                    data-testid="demo-credentials-button"
                  >
                    Use Demo {userType === 'barber' ? 'Barber' : 'Customer'}
                  </button>
                  <button type="button" className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                    Forgot Password?
                  </button>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="signin-submit-button"
                  className="w-full h-12 bg-[#00C167] hover:bg-[#009E54] text-white font-semibold"
                >
                  {loading ? 'Signing In...' : `Sign In as ${userType === 'barber' ? 'Barber' : 'Customer'}`}
                </Button>
              </form>
            </TabsContent>

            {/* Sign Up Form */}
            <TabsContent value="signup">
              {userType === 'customer' ? (
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div>
                    <Label htmlFor="signup-name" className="text-[var(--text-primary)]">Full Name</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="signup-name"
                        data-testid="signup-name-input"
                        type="text"
                        required
                        placeholder="John Doe"
                        value={signUpData.full_name}
                        onChange={(e) => setSignUpData({ ...signUpData, full_name: e.target.value })}
                        className="pl-10 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)] focus:border-[#00C167] focus:ring-[#00C167]/20"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="signup-email" className="text-[var(--text-primary)]">Email</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="signup-email"
                        data-testid="signup-email-input"
                        type="email"
                        required
                        placeholder="your@email.com"
                        value={signUpData.email}
                        onChange={(e) => setSignUpData({ ...signUpData, email: e.target.value })}
                        className="pl-10 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)] focus:border-[#00C167] focus:ring-[#00C167]/20"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="signup-phone" className="text-[var(--text-primary)]">Phone Number</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="signup-phone"
                        data-testid="signup-phone-input"
                        type="tel"
                        required
                        placeholder="+234 800 000 0000"
                        value={signUpData.phone}
                        onChange={(e) => setSignUpData({ ...signUpData, phone: e.target.value })}
                        className="pl-10 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)] focus:border-[#00C167] focus:ring-[#00C167]/20"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="signup-password" className="text-[var(--text-primary)]">Password</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="signup-password"
                        data-testid="signup-password-input"
                        type="password"
                        required
                        placeholder="••••••••"
                        value={signUpData.password}
                        onChange={(e) => setSignUpData({ ...signUpData, password: e.target.value })}
                        className="pl-10 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)] focus:border-[#00C167] focus:ring-[#00C167]/20"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="signup-confirm" className="text-[var(--text-primary)]">Confirm Password</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="signup-confirm"
                        data-testid="signup-confirm-input"
                        type="password"
                        required
                        placeholder="••••••••"
                        value={signUpData.confirm_password}
                        onChange={(e) => setSignUpData({ ...signUpData, confirm_password: e.target.value })}
                        className="pl-10 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)] focus:border-[#00C167] focus:ring-[#00C167]/20"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="signup-submit-button"
                    className="w-full h-12 bg-[#00C167] hover:bg-[#009E54] text-white font-semibold"
                  >
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                </form>
              ) : (
                <div className="text-center py-8">
                  <Scissors className="w-16 h-16 text-[#00C167] mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-[var(--text-primary)] mb-2">Become a Barber</h3>
                  <p className="text-[var(--text-secondary)] mb-6">
                    To join as a barber, please use the dedicated barber signup form.
                  </p>
                  <Button
                    onClick={() => {
                      onClose();
                      document.getElementById('for-barbers')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="bg-[#00C167] hover:bg-[#009E54] text-white"
                  >
                    Go to Barber Application
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Footer */}
        <div className="border-t border-[var(--border-color)] p-4 text-center text-sm text-[var(--text-secondary)]">
          By continuing, you agree to BarberGo's Terms of Service and Privacy Policy
        </div>
      </div>
    </div>
  );
};

export default AuthModal;